import { createContext, useReducer, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { buildAbilityFor, defaultAbility } from 'contexts/ability';
import type { AppAbility } from 'contexts/ability';
import type { AppRole } from 'types/ability';

// ==============================|| AUTH TYPES ||============================== //

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: AppRole;
}

export interface AuthState {
  isLoggedIn: boolean;
  isInitialized: boolean;
  user: AuthUser | null;
}

export interface AuthContextType extends AuthState {
  login: (email: string) => void;
  logout: () => void;
  ability: AppAbility;
}

// ==============================|| REDUCER ||============================== //

type AuthAction =
  | { type: 'INIT'; payload: { isLoggedIn: boolean; user: AuthUser | null } }
  | { type: 'LOGIN'; payload: { user: AuthUser } }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'INIT':
      return { ...state, isInitialized: true, isLoggedIn: action.payload.isLoggedIn, user: action.payload.user };
    case 'LOGIN':
      return { ...state, isLoggedIn: true, user: action.payload.user };
    case 'LOGOUT':
      return { ...state, isLoggedIn: false, user: null };
    default:
      return state;
  }
}

// ==============================|| CONTEXT ||============================== //

const AuthContext = createContext<AuthContextType>({
  ...initialState,
  login: () => {},
  logout: () => {},
  ability: defaultAbility
});

export default AuthContext;

// ==============================|| PROVIDER ||============================== //

const STORAGE_KEY = 'yora_logged_in';
const EMAIL_KEY = 'yora_user_email';

function buildUserFromEmail(email: string): AuthUser {
  return {
    id: email,
    name: email.split('@')[0],
    email,
    role: email.endsWith('@hiyora.com') ? 'super_admin' : 'tenant_admin'
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const ability = useMemo(() => {
    if (state.isLoggedIn && state.user) {
      return buildAbilityFor(state.user.role);
    }
    return defaultAbility;
  }, [state.isLoggedIn, state.user]);

  // On mount, check localStorage for existing session
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const email = localStorage.getItem(EMAIL_KEY);
    if (stored === 'true' && email) {
      dispatch({ type: 'INIT', payload: { isLoggedIn: true, user: buildUserFromEmail(email) } });
    } else {
      dispatch({ type: 'INIT', payload: { isLoggedIn: false, user: null } });
    }
  }, []);

  const login = useCallback((email: string) => {
    localStorage.setItem(STORAGE_KEY, 'true');
    localStorage.setItem(EMAIL_KEY, email);
    dispatch({ type: 'LOGIN', payload: { user: buildUserFromEmail(email) } });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(EMAIL_KEY);
    dispatch({ type: 'LOGOUT' });
  }, []);

  return <AuthContext.Provider value={{ ...state, login, logout, ability }}>{children}</AuthContext.Provider>;
}
